/**
 * De leesbare persona-gids (Jordy 07-10-2026, punt 5).
 *
 * Lezen: owner, admin, manager, en een chatter voor zijn eigen modellen. De
 * server haalt "Te bevestigen door model" en "Twijfels en keuzes" er voor
 * iedereen behalve owner/admin al uit; dit scherm toont wat het krijgt.
 * Bewerken (tekst en status) alleen owner/admin: `kan_bewerken` van de server.
 *
 * De tekst is markdown. Hij wordt hier naar React-elementen omgezet (koppen,
 * opsommingen, genummerde regels, vet), nooit als HTML in de pagina gezet.
 */
import { Fragment, useState } from 'react'
import type { ReactNode } from 'react'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { ErrorNote, Loading } from './ui'

interface Gids {
  persona_id: number
  tekst: string
  status: 'draft' | 'confirmed' | null
  kan_bewerken: boolean
}

/** **vet** binnen een regel; de rest letterlijk. */
function inline(tekst: string): ReactNode[] {
  return tekst.split(/(\*\*[^*]+\*\*)/g).map((deel, i) =>
    deel.startsWith('**') && deel.endsWith('**') && deel.length > 4
      ? <b key={i}>{deel.slice(2, -2)}</b>
      : <Fragment key={i}>{deel}</Fragment>)
}

/** Kleine markdown-weergave: #-koppen, - en 1. lijsten, alinea's. */
export function Markdown({ tekst }: { tekst: string }) {
  const blokken: ReactNode[] = []
  let lijst: { soort: 'ul' | 'ol'; items: { diep: boolean; tekst: string }[] } | null = null
  const sluitLijst = () => {
    if (!lijst) return
    const L = lijst.soort
    const items = lijst.items
    blokken.push(
      <L key={`l${blokken.length}`} style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
        {items.map((it, i) => (
          <li key={i} style={it.diep ? { marginLeft: 16, listStyle: 'circle' } : undefined}>
            {inline(it.tekst)}
          </li>
        ))}
      </L>)
    lijst = null
  }
  for (const regel of tekst.split('\n')) {
    const kop = regel.match(/^(#{1,6})\s+(.*)$/)
    const ul = regel.match(/^(\s*)[-*]\s+(.*)$/)
    const ol = regel.match(/^(\s*)\d+[.)]\s+(.*)$/)
    if (kop) {
      sluitLijst()
      const n = kop[1].length
      blokken.push(
        <div key={`k${blokken.length}`}
             style={{ fontWeight: 700, fontSize: n <= 1 ? 16 : n === 2 ? 14 : 13,
                      margin: n <= 2 ? '14px 0 6px' : '10px 0 4px' }}>
          {inline(kop[2])}
        </div>)
    } else if (ul || ol) {
      const soort = ul ? 'ul' : 'ol'
      const m = (ul ?? ol)!
      if (!lijst || lijst.soort !== soort) { sluitLijst(); lijst = { soort, items: [] } }
      lijst.items.push({ diep: m[1].length >= 2, tekst: m[2] })
    } else if (!regel.trim()) {
      sluitLijst()
    } else {
      sluitLijst()
      blokken.push(<p key={`p${blokken.length}`} style={{ margin: '4px 0' }}>{inline(regel)}</p>)
    }
  }
  sluitLijst()
  return <div className="persona-gids" style={{ fontSize: 13, lineHeight: 1.5 }}>{blokken}</div>
}

export function ConceptBanner() {
  return (
    <div role="note" style={{ margin: '0 0 10px', padding: '8px 12px', borderRadius: 8,
                              border: '1px solid rgba(245,158,11,0.45)',
                              background: 'rgba(245,158,11,0.10)', fontSize: 12.5, fontWeight: 600 }}>
      DRAFT, not yet confirmed by the model
    </div>
  )
}

/** De gids van één model, met bewerken voor owner/admin. */
export default function PersonaGids({ personaId }: { personaId: number | string }) {
  const g = useApi<Gids>(`/api/admin/models/personas/${personaId}/persona-gids`, [personaId])
  const { run, busy } = useActions()
  const [bewerk, setBewerk] = useState<{ tekst: string; status: string } | null>(null)

  if (g.loading && !g.data) return <Loading />
  if (g.error) return <ErrorNote error={g.error} />
  const d = g.data
  if (!d) return null

  if (bewerk) {
    return (
      <div>
        <div className="row" style={{ gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <label className="mut2" style={{ fontSize: 12 }}>Status</label>
          <select className="inp" style={{ maxWidth: 200 }} value={bewerk.status}
                  onChange={(e) => setBewerk({ ...bewerk, status: e.target.value })}>
            <option value="draft">Draft</option>
            <option value="confirmed">Confirmed by the model</option>
          </select>
          <span className="spacer" style={{ flex: 1 }} />
          <button className="btn ghost sm" type="button" onClick={() => setBewerk(null)}>Cancel</button>
          <button className="btn primary sm" type="button" disabled={busy === 'Persona guide'}
                  onClick={() => run({
                    label: 'Persona guide',
                    request: mut.put(`/api/admin/models/personas/${personaId}/persona-gids`,
                                     { tekst: bewerk.tekst, status: bewerk.status }),
                    onDone: () => { setBewerk(null); g.refetch() },
                    successText: 'Persona guide saved.',
                  })}>
            Save
          </button>
        </div>
        <textarea className="inp mono" rows={24} value={bewerk.tekst}
                  onChange={(e) => setBewerk({ ...bewerk, tekst: e.target.value })} />
      </div>
    )
  }

  return (
    <div>
      {d.status === 'draft' ? <ConceptBanner /> : null}
      {d.kan_bewerken ? (
        <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 6 }}>
          <button className="btn ghost sm" type="button"
                  onClick={() => setBewerk({ tekst: d.tekst, status: d.status ?? 'draft' })}>
            Edit guide
          </button>
        </div>
      ) : null}
      {d.tekst.trim()
        ? <Markdown tekst={d.tekst} />
        : <div className="mut2" style={{ fontSize: 12.5 }}>No persona guide yet.</div>}
    </div>
  )
}
